import { useAppContext } from '@/AppContext'
import { mayNeedRestartTrigger } from '@/components/SessionSettings/SessionSettings'
import PulseIcon from '@/CustomIcons/PulseIcon'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import { getAudioAndVideoMediaSources } from '@/services/MediaDevices'
import useAnalytics from '@/services/useAnalytics'
import { Checkbox } from '@/UIComponents2/Checkbox'
import { useUserContext } from '@/UserContext'
import { Card } from '../../Card'
import { CardHeader } from '../../CardHeader'

export function AdvancedOptionsSection() {
    const [appState, appDispatch] = useAppContext()
    const [_userState, userDispatch] = useUserContext()
    const { logUserActivityWithContext } = useAnalytics()

    function handleShowAllAudioDevicesChange(value: boolean) {
        appDispatch({
            type: 'update_app_settings',
            value: { forceShowAllAudioDevices: value },
        })

        logUserActivityWithContext(AnalyticsEvents.TOGGLED_SHOW_ALL_AUDIO_DEVICES, {
            forceShowAllAudioDevices: value,
        })
        mayNeedRestartTrigger.trigger()

        userDispatch({
            type: 'update_media_selection',
            value: {
                audioInputs: [],
                audioOutput: '_undef',
            },
        })

        getAudioAndVideoMediaSources({
            origin: 'forceShowAllAudioDevicesChanged',
            forceShowAllAudioDevices: appState.appSettings.forceShowAllAudioDevices,
        }).then((mediaSources) => {
            userDispatch({
                type: 'update_media_sources',
                value: mediaSources,
            })
        })
    }

    function handleSuppressDeviceChangePopupsChange(value: boolean) {
        appDispatch({
            type: 'update_app_settings',
            value: { noMediaSourcesChangedPrompt: value },
        })
        logUserActivityWithContext(AnalyticsEvents.TOGGLED_DISABLE_DEVICE_CHANGE_PROMPTS, {
            noMediaSourcesChangedPrompt: value,
        })
    }

    function handleUseSystemDefaultSpeakerChange(value: boolean) {
        appDispatch({
            type: 'update_app_settings',
            value: { selectSystemDefaultForAudioOutputOnStartup: value },
        })
        logUserActivityWithContext(AnalyticsEvents.TOGGLED_SELECT_SYSTEM_DEFAULT_OUTPUT_ON_STARTUP, {
            selectSystemDefaultForAudioOutputOnStartup: value,
        })
    }

    function handlePreventVolumeAdjustmentChange(value: boolean) {
        appDispatch({
            type: 'update_app_settings',
            value: { preventVolumeAutoAdjustment: value },
        })
        logUserActivityWithContext(AnalyticsEvents.TOGGLED_PREVENT_VOLUME_AUTO_ADJUSTMENT, {
            preventVolumeAutoAdjustment: value,
        })
        mayNeedRestartTrigger.trigger()
    }

    function handleRemoteRecordingDisabledChange(value: boolean) {
        appDispatch({
            type: 'update_app_settings',
            value: { remoteRecordingDisabled: value },
        })
        logUserActivityWithContext(AnalyticsEvents.TOGGLED_DISABLE_REMOTE_RECORDING, {
            remoteRecordingDisabled: value,
        })
    }

    return (
        <Card className="flex flex-col gap-[20px]">
            <CardHeader icon={PulseIcon} title="Advanced Options" />
            <Checkbox
                label="Show all audio devices (may show duplicate devices)"
                checked={appState.appSettings.forceShowAllAudioDevices}
                labelClassName="font-light"
                onChange={handleShowAllAudioDevicesChange}
            />
            <Checkbox
                label="Automatically change system audio output on sharing computer audio"
                checked={appState.appSettings.autoChangeSystemAudioOutputDevice}
                onChange={() => {
                    appDispatch({
                        type: 'update_app_settings',
                        value: {
                            autoChangeSystemAudioOutputDevice: !appState.appSettings.autoChangeSystemAudioOutputDevice,
                        },
                    })
                }}
            />
            <Checkbox
                label="Don't show popups when a device is added or removed"
                checked={appState.appSettings.noMediaSourcesChangedPrompt}
                labelClassName="font-light"
                onChange={handleSuppressDeviceChangePopupsChange}
            />
            <Checkbox
                label="Select system default for speaker on app startup"
                checked={appState.appSettings.selectSystemDefaultForAudioOutputOnStartup}
                labelClassName="font-light"
                onChange={handleUseSystemDefaultSpeakerChange}
            />
            <Checkbox
                label="Prevent volume auto adjustment"
                checked={appState.appSettings.preventVolumeAutoAdjustment}
                labelClassName="font-light"
                onChange={handlePreventVolumeAdjustmentChange}
            />
            <Checkbox
                label="Prevent collaborators from remote-recording your audio devices"
                checked={appState.appSettings.remoteRecordingDisabled}
                labelClassName="font-light"
                onChange={handleRemoteRecordingDisabledChange}
            />
        </Card>
    )
}
